import 'regenerator-runtime/runtime.js';
import { createAccreditationApi } from './accreditation-api.js';
import { createAccreditationScanner } from './accreditation-scanner.js';

const WRISTBAND_IMAGES = {
    red: 'tds-2027-paska-adventure-1400-main.jpg',
    blue: 'tds-2027-paska-relaxed-1400-main.jpg',
    brown: 'tds-2027-paska-active-1400-main.jpg',
    yellow: 'tds-2027-paska-adventure-1430-main.jpg',
    pink: 'tds-2027-paska-relaxed-1430-main.jpg',
    green: 'tds-2027-paska-active-1430-main.jpg',
};

export function initAccreditation() {
    const root = document.querySelector('[data-accreditation]');
    if (!root) return;
    const find = (selector, parent = root) => parent.querySelector(selector);
    const all = (selector, parent = root) => Array.from(parent.querySelectorAll(selector));
    const messages = {};
    all('[data-message]').forEach(element => { messages[element.dataset.message] = element.textContent; });
    const text = (key, values = {}) => (messages[key] || key).replace(/\{(\w+)\}/g, (match, name) => values[name] === undefined ? match : values[name]);
    const memory = {};
    const storage = { getItem(key) { try { return sessionStorage.getItem(key); } catch (error) { return memory[key] || null; } }, setItem(key, value) { try { sessionStorage.setItem(key, value); } catch (error) { memory[key] = value; } } };
    const draftKey = 'tds-accreditation-v2-live';
    let draft;
    try { draft = JSON.parse(storage.getItem(draftKey) || '{}'); } catch (error) { draft = {}; }
    if (!draft || typeof draft !== 'object') draft = {};
    draft.forms = draft.forms || {};
    const persist = () => storage.setItem(draftKey, JSON.stringify(draft));
    draft.invites = draft.invites || {};
    draft.management = draft.management || {};
    const api = createAccreditationApi(root.dataset.apiUrl);
    let order, saving = false, expiryTimer;
    const scanner = find('[data-qr-dialog]') ? createAccreditationScanner(root, text) : null;
    const params = new URLSearchParams(location.search);
    function url(name, extras = {}) {
        const target = new URL(root.dataset[name + 'Url'], location.origin);
        Object.entries(extras).forEach(([key, value]) => target.searchParams.set(key, value));
        return target.href;
    }
    function go(name, extras) { persist(); location.assign(url(name, extras)); }
    function request(action, data = {}) {
        return Promise.resolve().then(() => {
            if (!api || typeof api[action] !== 'function') throw { key: 'errors.service_unavailable', notSent: true };
            return api[action]({ locale: root.dataset.locale, context: order ? order.context : draft.context, ...data });
        });
    }
    function notice(element, key, error = false, values = {}) {
        if (!element) return;
        element.hidden = !key;
        element.textContent = key ? text(key, values) : '';
        if (['errors.order_cancelled', 'errors.cancelled_or_refunded'].includes(key)) {
            const address = 'info@store.bubo.sk';
            const parts = element.textContent.split(address);
            if (parts.length === 2) {
                const link = document.createElement('a');
                link.href = 'mailto:' + address;
                link.textContent = address;
                element.replaceChildren(document.createTextNode(parts[0]), link, document.createTextNode(parts[1]));
            }
        }
        element.classList.toggle('is-error', error);
    }
    function errors(form, values) {
        all('[data-error]', form).forEach(element => {
            const key = values[element.dataset.error];
            element.hidden = !key; element.textContent = key ? text(key) : '';
            const fields = element.dataset.error === 'birthDate' ? all('[data-birth-part]', form) : [form.elements.namedItem(element.dataset.error)].filter(Boolean);
            fields.forEach(field => field.setAttribute('aria-invalid', key ? 'true' : 'false'));
        });
        const first = find('[aria-invalid="true"]:not(:disabled)', form);
        if (first) first.focus();
    }
    function updateDate(form, savedDay) {
        const year = find('[data-birth-part="year"]', form), month = find('[data-birth-part="month"]', form), day = find('[data-birth-part="day"]', form);
        if (!year) return;
        const previous = savedDay === undefined ? day.value : savedDay;
        month.disabled = !year.value;
        if (!year.value) month.value = '';
        const count = year.value && month.value ? new Date(Number(year.value), Number(month.value), 0).getDate() : 0;
        day.disabled = !count; day.options.length = 1;
        for (let i = 1; i <= count; i++) day.add(new Option(String(i).padStart(2, '0'), String(i)));
        day.value = Number(previous) > 0 && Number(previous) <= count ? String(Number(previous)) : '';
    }
    function read(form) {
        const values = {};
        all('input[name], select[name]', form).forEach(input => { values[input.name] = input.type === 'checkbox' ? input.checked : input.value.trim(); });
        if (find('[data-birth-date]', form)) values.birthDate = ['day', 'month', 'year'].map(part => find('[data-birth-part="' + part + '"]', form).value).map(value => value ? value.padStart(2, '0') : '').join('. ');
        if ('code' in values) values.code = values.code.toUpperCase();
        return values;
    }
    function fill(form, values = {}) {
        all('input[name], select[name]', form).forEach(input => { if (input.type === 'checkbox') input.checked = values[input.name] === true; else input.value = values[input.name] || ''; });
        if (find('[data-birth-date]', form)) {
            const parts = (values.birthDate || '').split(/\.\s*/);
            find('[data-birth-part="year"]', form).value = parts[2] || '';
            find('[data-birth-part="month"]', form).value = String(Number(parts[1]) || '');
            updateDate(form, parts[0]);
        }
        updateQrState(form);
        updateConsents(form);
    }

    function updateConsents(form) {
        const container = find('[data-minor-consent]', form);
        if (!container) return;

        const year = Number(find('[data-birth-part="year"]', form).value);
        const month = Number(find('[data-birth-part="month"]', form).value);
        const day = Number(find('[data-birth-part="day"]', form).value);
        const birth = new Date(Date.UTC(year, month - 1, day));
        const today = root.dataset.ageDate;
        const birthday = [String(year).padStart(4, '0'), String(month).padStart(2, '0'), String(day).padStart(2, '0')].join('-');
        const eighteenthBirthday = String(year + 18) + birthday.slice(4);
        const valid = year >= 1900 && month > 0 && day > 0 && birth.getUTCFullYear() === year && birth.getUTCMonth() === month - 1 && birth.getUTCDate() === day && birthday <= today;
        const minor = valid && eighteenthBirthday > today;
        const checkbox = form.elements.minorSupervision;
        container.hidden = !minor;
        checkbox.required = minor;
        checkbox.disabled = !minor;
        if (!minor) {
            checkbox.checked = false;
            checkbox.removeAttribute('aria-invalid');
            find('[data-error="minorSupervision"]', form).hidden = true;
        }
    }

    function updateQrState(form) {
        const button = find('[data-scan]', form);
        if (!button) return;

        const identifier = form.elements.qrIdentifier.value;
        const loaded = Boolean(identifier);
        button.classList.toggle('is-scanned', loaded);
        find('[data-scan-idle]', button).hidden = loaded;
        find('[data-scan-icon]', button).hidden = loaded;
        find('[data-scan-loaded]', button).hidden = !loaded;
        find('[data-scan-check]', button).hidden = !loaded;
        find('[data-scan-code]', button).textContent = identifier.slice(-6);
        const status = find('[data-qr-status]', form);
        status.hidden = !loaded;
        status.textContent = loaded ? text('states.qr_loaded', { code: identifier.slice(-6) }) : '';
    }
    function validate(form, values) {
        const found = {};
        all('[required]:not(:disabled)', form).forEach(input => {
            if (input.type === 'checkbox' ? !input.checked : !input.value.trim()) {
                found[input.dataset.birthPart ? 'birthDate' : input.name] = input.type === 'checkbox' ? 'consent.required' : 'validation.required';
            }
        });
        if ('code' in values && !values.qrIdentifier && !/^[A-Z0-9]{6}$/.test(values.code)) found.code = 'validation.code';
        if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) found.email = 'validation.email';
        if ('birthDate' in values) {
            const parts = values.birthDate.match(/^(\d{2})\. (\d{2})\. (\d{4})$/);
            const date = parts && new Date(Number(parts[3]), Number(parts[2]) - 1, Number(parts[1]));
            if (!date || date.getFullYear() !== Number(parts[3]) || date.getMonth() !== Number(parts[2]) - 1 || date.getDate() !== Number(parts[1]) || date > new Date()) found.birthDate = 'validation.birth_date';
        }
        errors(form, found);
        return Object.keys(found).length === 0;
    }
    function setProgress(step) {
        all('[data-progress-step]').forEach(item => {
            const number = Number(item.dataset.progressStep);
            item.classList.toggle('is-current', number === step);
            item.classList.toggle('is-complete', number < step);

            if (number === step) {
                item.setAttribute('aria-current', 'step');
            } else {
                item.removeAttribute('aria-current');
            }
        });
    }

    function guard(key) {
        scanner?.close();
        clearTimeout(expiryTimer);
        draft.accessNotice = key;
        persist();
        location.replace(url('status'));
    }
    function ticketLabel(ticket) { return text('colors.' + ticket.colorKey) + ' · ' + ticket.route + ' · ' + ticket.startTime; }

    function renderTicketImage(card, ticket) {
        const filename = Object.prototype.hasOwnProperty.call(WRISTBAND_IMAGES, ticket.colorKey) ? WRISTBAND_IMAGES[ticket.colorKey] : null;
        if (!filename) return;

        const image = find('[data-card-image]', card);
        image.src = image.dataset.imageBase + filename;
        find('[data-card-art]', card).hidden = false;
    }

    function lock(form, locked) {
        all('input, select, button', form).forEach(control => { control.disabled = locked; });
        if (!locked) { updateDate(form); updateConsents(form); }
    }
    function renderOrder(value, focusTicket) {
        scanner?.close();
        setProgress(3);
        order = value;
        if (draft.accessIdentity && draft.accessIdentity !== order.accessIdentity) { draft.forms = {}; draft.invites = {}; draft.management = {}; }
        draft.orderId = order.id; draft.context = order.context; draft.accessIdentity = order.accessIdentity;
        const visibleIds = new Set(order.tickets.map(ticket => ticket.id));
        Object.keys(draft.forms).filter(id => !visibleIds.has(id)).forEach(id => delete draft.forms[id]);
        Object.keys(draft.invites).filter(id => !visibleIds.has(id)).forEach(id => delete draft.invites[id]);
        Object.keys(draft.management).filter(id => !visibleIds.has(id)).forEach(id => delete draft.management[id]);
        persist();
        find('[data-dashboard-guard]').hidden = true; find('[data-dashboard]').hidden = false;
        find('[data-dashboard-intro]').textContent = text(order.readOnly ? 'dashboard.preview_intro' : order.role === 'recipient' ? 'dashboard.recipient_intro' : 'dashboard.intro');
        find('[data-order-title]').textContent = text('dashboard.order', { number: order.orderNumber });
        const expires = new Date(order.sessionExpiresAt).toLocaleString(root.dataset.locale === 'en' ? 'en-GB' : 'sk-SK', { dateStyle: 'short', timeStyle: 'short' });
        find('[data-session-label]').textContent = text('dashboard.session', { time: expires });
        const validTickets = order.tickets.filter(ticket => ticket.status !== 'cancelled');
        const completed = validTickets.filter(ticket => ticket.status === 'accredited').length;
        find('[data-order-progress]').textContent = text('dashboard.progress', { done: completed, total: validTickets.length });
        notice(find('[data-order-notice]'), order.status === 'paid' ? (!order.tickets.length ? 'dashboard.empty' : null) : order.status === 'unpaid' ? 'errors.unpaid' : ['cancelled', 'refunded'].includes(order.status) ? 'errors.order_cancelled' : 'errors.service_unavailable', order.status !== 'paid');
        if (order.forwarded) notice(find('[data-order-notice]'), order.forwarded === 'failed' ? 'manage.forward_failed' : 'manage.forwarded', order.forwarded === 'failed');
        notice(find('[data-read-only-notice]'), order.readOnly ? 'dashboard.not_open' : null);
        find('[data-all-done]').hidden = !validTickets.length || completed !== validTickets.length || order.status !== 'paid';
        find('[data-all-done]').textContent = text(order.role === 'recipient' ? 'dashboard.recipient_all_done' : 'dashboard.all_done');
        const cards = find('[data-ticket-cards]'), list = find('[data-order-list]');
        cards.replaceChildren(); list.replaceChildren();
        order.tickets.forEach((ticket, index) => {
            const card = find('[data-ticket-template]').content.firstElementChild.cloneNode(true);
            const prefix = 'ticket-' + index;
            card.id = prefix; card.dataset.ticketId = ticket.id;
            all('[id]', card).forEach(element => { element.id = element.id.replace(/^slot-/, prefix + '-'); });
            all('[for], [aria-describedby]', card).forEach(element => ['for', 'aria-describedby'].forEach(attr => { if (element.hasAttribute(attr)) element.setAttribute(attr, element.getAttribute(attr).replace(/slot-/g, prefix + '-')); }));
            all('input[autocomplete], select[autocomplete]', card).forEach(input => { if (!['off', 'on'].includes(input.autocomplete)) input.autocomplete = 'section-' + prefix + ' ' + input.autocomplete; });
            const saved = ticket.status === 'accredited';
            notice(find('[data-confirmation-feedback]', card), value.confirmationMailFailed === ticket.id ? 'errors.confirmation_mail_failed' : null, true);
            const delegated = order.role === 'owner' && !!ticket.delegation;
            const canAccredit = ticket.canAccredit === true;
            if (delegated) { delete draft.forms[ticket.id]; delete draft.invites[ticket.id]; }
            const stateKey = order.status === 'paid' ? saved ? 'ticket.accredited' : ticket.status === 'cancelled' ? 'ticket.cancelled' : delegated ? 'invitation.assigned' : 'ticket.available' : 'ticket.blocked';
            card.classList.toggle('is-accredited', saved);
            find('[data-card-title]', card).textContent = text('ticket.title', { number: ticket.number });
            find('[data-card-status]', card).textContent = text(stateKey);
            find('[data-card-variant]', card).textContent = ticketLabel(ticket);
            renderTicketImage(card, ticket);
            find('[data-card-swatch]', card).style.backgroundColor = /^#[a-f0-9]{6}$/i.test(ticket.color) ? ticket.color : '#777777';
            find('[data-card-band]', card).textContent = saved && ticket.code ? text('ticket.assigned', { code: ticket.code }) : saved ? text('invitation.completed') : text('ticket.unassigned');
            find('[data-card-band]', card).hidden = saved && delegated;
            const form = find('[data-ticket-form]', card);
            form.dataset.ticketId = ticket.id;
            form.hidden = saved || delegated || order.readOnly === true;
            find('[data-card-saved]', card).hidden = !saved || delegated;
            if (saved) {
                find('[data-saved-name]', card).textContent = ticket.participant ? ticket.participant.firstName + ' ' + ticket.participant.lastName : text('invitation.completed');
                delete draft.forms[ticket.id];
            } else {
                const savedDraft = draft.forms[ticket.id] || {};
                fill(form, savedDraft.values);
                bindTicket(form, ticket);
                if (savedDraft.pending) { lock(form, true); find('[data-check-operation]', form).hidden = false; find('[data-check-operation]', form).disabled = false; notice(find('[data-card-feedback]', form), 'errors.unknown', true); }
                if (!canAccredit || (draft.invites[ticket.id] && draft.invites[ticket.id].pending)) lock(form, true);
            }
            bindManagement(card, ticket);
            bindInvitation(card, ticket, delegated);
            const item = document.createElement('li'), link = document.createElement('a'), title = document.createElement('strong'), status = document.createElement('span');
            link.href = '#' + prefix;
            title.textContent = text('ticket.title', { number: ticket.number }) + ' — ' + ticketLabel(ticket);
            status.textContent = text(stateKey) + (delegated ? ' · ' + ticket.delegation.email : saved && ticket.participant ? ' · ' + ticket.participant.firstName + ' ' + ticket.participant.lastName + ' · ' + ticket.code : '');
            link.append(title, status);
            link.addEventListener('click', event => { event.preventDefault(); card.focus(); card.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
            item.classList.toggle('is-accredited', saved); item.append(link); list.append(item); cards.append(card);
            if (focusTicket === ticket.id) requestAnimationFrame(() => card.focus({ preventScroll: true }));
        });
        clearTimeout(expiryTimer);
        const remaining = order.sessionExpiresAt - Date.now();
        if (remaining <= 0) { guard('errors.session_expired'); return; }
        expiryTimer = setTimeout(() => guard('errors.session_expired'), Math.min(remaining, 2147483647));
        persist();
    }
    async function loadOrder() {
        try { renderOrder(await request('getOrder')); } catch (error) { guard(error.key || 'errors.service_unavailable'); }
    }
    function saveDraft(form) {
        const id = form.dataset.ticketId;
        draft.forms[id] = { ...draft.forms[id], values: read(form) }; persist();
    }
    function bindManagement(card, ticket) {
        const panel = find('[data-registration-management]', card);
        panel.hidden = !ticket.canManageRegistration;
        if (!ticket.canManageRegistration) {
            delete draft.management[ticket.id];
            return;
        }
        let cached = draft.management[ticket.id];
        if (cached && cached.registrationId !== ticket.registrationId) {
            delete draft.management[ticket.id];
            cached = null;
        }
        const form = find('[data-edit-form]', panel);
        const details = find('[data-edit-panel]', panel);
        const feedback = find('[data-management-feedback]', panel);
        const retry = find('[data-retry-management]', panel);
        const reload = find('[data-reload-management]', panel);
        fill(form, cached?.values || ticket.participant);
        details.open = !!cached?.open;
        const capture = () => {
            const previous = draft.management[ticket.id];
            draft.management[ticket.id] = {
                registrationId: ticket.registrationId,
                revision: previous?.revision ?? ticket.revision,
                values: read(form), open: details.open, ...previous,
            };
            draft.management[ticket.id].values = read(form);
            draft.management[ticket.id].open = details.open;
            persist();
        };
        form.addEventListener('input', event => {
            if (event.target.dataset.birthPart && event.target.dataset.birthPart !== 'day') updateDate(form);
            updateConsents(form);
            capture();
        });
        details.addEventListener('toggle', () => { if (details.isConnected) capture(); });
        const showPending = () => {
            all('input, select, button', panel).forEach(control => { control.disabled = true; });
            retry.hidden = false;
            retry.disabled = false;
            notice(feedback, 'manage.uncertain', true);
        };
        const showConflict = () => {
            lock(form, true);
            find('[data-cancel-registration]', panel).disabled = true;
            reload.hidden = false;
            notice(feedback, 'errors.registration_changed', true);
        };
        if (cached?.pending) showPending();
        else if (cached && cached.revision !== ticket.revision) showConflict();
        const run = async (action, payload) => {
            if (saving) return;
            saving = true;
            capture();
            draft.management[ticket.id].pending = { action, payload };
            persist();
            all('input, select, button', panel).forEach(control => { control.disabled = true; });
            notice(feedback, 'ticket.saving');
            try {
                const result = await request(action, payload);
                delete draft.management[ticket.id];
                delete draft.forms[ticket.id];
                persist();
                renderOrder(result, ticket.id);
                notice(find('[data-order-notice]'), action === 'cancelRegistration' ? 'manage.cancelled' : 'manage.updated');
            } catch (error) {
                if (!error.key && !error.notSent) {
                    showPending();
                } else {
                    delete draft.management[ticket.id].pending;
                    persist();
                    if (['errors.session_expired', 'errors.session_changed'].includes(error.key)) guard(error.key);
                    else if (error.key === 'errors.registration_changed') {
                        showConflict();
                        reload.disabled = false;
                    } else {
                        all('button', panel).forEach(control => { control.disabled = false; });
                        lock(form, false);
                        retry.hidden = true;
                        errors(form, error.fields || {});
                        notice(feedback, error.key || 'errors.service_unavailable', true);
                        if (['errors.ticket_unavailable', 'errors.ticket_delegated', 'errors.invitation_unavailable', 'errors.order_cancelled', 'errors.unpaid'].includes(error.key)) await loadOrder();
                    }
                }
            } finally { saving = false; }
        };
        const start = action => {
            if (saving || draft.management[ticket.id]?.pending) return;
            const participant = read(form);
            if (action === 'updateRegistration' && !validate(form, participant)) return;
            const payload = { ticketId: ticket.id, registrationId: ticket.registrationId,
                revision: draft.management[ticket.id]?.revision ?? ticket.revision, operationId: crypto.randomUUID() };
            if (action === 'updateRegistration') payload.participant = participant;
            run(action, payload);
        };
        form.addEventListener('submit', event => { event.preventDefault(); start('updateRegistration'); });
        find('[data-cancel-registration]', panel).addEventListener('click', () => start('cancelRegistration'));
        retry.addEventListener('click', () => {
            const pending = draft.management[ticket.id]?.pending;
            if (pending) run(pending.action, pending.payload);
        });
        reload.addEventListener('click', () => {
            if (saving) return;
            delete draft.management[ticket.id];
            persist();
            loadOrder();
        });
    }

    function bindInvitation(card, ticket, delegated) {
        const panel = find('[data-invite-panel]', card), form = find('[data-invite-form]', card);
        panel.hidden = !ticket.canInvite || !!(draft.forms[ticket.id] && draft.forms[ticket.id].pending);
        if (order.role === 'recipient') find('p', panel).textContent = text('manage.forward_help');
        const cached = draft.invites[ticket.id] || {};
        fill(form, { email: cached.pending ? cached.pending.email : cached.email });
        if (cached.pending) {
            panel.open = true;
            form.elements.email.disabled = true;
            notice(find('[data-invite-feedback]', form), 'invitation.uncertain', true);
        }
        form.addEventListener('input', () => { draft.invites[ticket.id] = { email: form.elements.email.value }; persist(); });
        form.addEventListener('submit', async event => {
            event.preventDefault();
            if (saving) return;
            const existing = draft.invites[ticket.id] || {};
            const values = read(form);
            if (!existing.pending && !validate(form, values)) return;
            const payload = existing.pending || { ticketId: ticket.id, email: values.email, operationId: crypto.randomUUID(), locale: root.dataset.locale };
            draft.invites[ticket.id] = { email: payload.email, pending: payload }; persist();
            saving = true; lock(form, true); lock(find('[data-ticket-form]', card), true);
            notice(find('[data-invite-feedback]', form), 'invitation.sending');
            try {
                const result = await request('inviteTicket', payload);
                delete draft.invites[ticket.id]; delete draft.forms[ticket.id]; persist();
                renderOrder(result, ticket.id);
            } catch (error) {
                if (error.key || error.notSent) {
                    delete draft.invites[ticket.id].pending; persist();
                    if (['errors.session_expired', 'errors.session_changed'].includes(error.key)) guard(error.key);
                    else {
                        lock(form, false); lock(find('[data-ticket-form]', card), !ticket.canAccredit);
                        errors(form, error.fields || {});
                        notice(find('[data-invite-feedback]', form), error.key || 'errors.service_unavailable', true);
                        if (['errors.ticket_delegated', 'errors.ticket_unavailable', 'errors.order_cancelled', 'errors.invitation_unavailable'].includes(error.key)) await loadOrder();
                    }
                } else {
                    notice(find('[data-invite-feedback]', form), 'invitation.uncertain', true);
                    find('button[type="submit"]', form).disabled = false;
                }
            } finally { saving = false; }
        });
        const box = find('[data-delegation]', card);
        box.hidden = !delegated;
        if (!delegated) return;
        find('[data-delegation-recipient]', card).textContent = text('invitation.assigned_to', { email: ticket.delegation.email });
        find('[data-delegation-delivery]', card).textContent = text(ticket.delegation.delivery === 'failed' ? 'invitation.delivery_failed' : ticket.delegation.delivery === 'pending' ? 'invitation.delivery_pending' : ticket.status === 'accredited' ? 'invitation.completed' : 'invitation.waiting');
        box.classList.toggle('is-error', ticket.delegation.delivery === 'failed');
        find('[data-delegation-actions]', card).hidden = !['available', 'accredited'].includes(ticket.status) || order.status !== 'paid';
        find('[data-revoke-invitation]', card).hidden = ticket.status !== 'available';
        find('[data-resend-invitation]', card).textContent = text(ticket.status === 'accredited' ? 'invitation.send_overview' : 'invitation.resend');
        const run = async (action, button) => {
            if (saving) return;
            saving = true; button.disabled = true;
            try {
                const result = await request(action, { ticketId: ticket.id, delegationId: ticket.delegation.id });
                renderOrder(result, ticket.id);
                if (action === 'resendInvitation') {
                    const updated = result.tickets.find(item => item.id === ticket.id);
                    const updatedCard = all('[data-ticket-cards] > [data-ticket-id]').find(item => item.dataset.ticketId === ticket.id);
                    if (updatedCard && updated?.delegation?.delivery === 'sent') notice(find('[data-delegation-feedback]', updatedCard), 'invitation.access_sent');
                }

            } catch (error) {
                if (['errors.session_expired', 'errors.session_changed'].includes(error.key)) guard(error.key);
                else {
                    notice(find('[data-delegation-feedback]', card), error.key || 'errors.unknown', true);
                    // Resolve an uncertain revoke from the server without repeating it against a new grant.
                    if (!error.key || ['errors.invitation_unavailable', 'errors.ticket_unavailable'].includes(error.key)) await loadOrder();
                }
            } finally { saving = false; button.disabled = false; }
        };
        const resend = find('[data-resend-invitation]', card), revoke = find('[data-revoke-invitation]', card);
        resend.addEventListener('click', () => run('resendInvitation', resend));
        revoke.addEventListener('click', () => run('revokeInvitation', revoke));
    }
    function bindTicket(form, ticket) {
        form.addEventListener('input', event => {
            if (event.target.dataset.birthPart && event.target.dataset.birthPart !== 'day') updateDate(form);
            if (event.target.name === 'code') {
                form.elements.qrIdentifier.value = '';
                updateQrState(form);
            }
            updateConsents(form);
            saveDraft(form);
        });
        find('[data-scan]', form).addEventListener('click', () => {
            const receive = identifier => {
                if (!form.isConnected || form.elements.qrIdentifier.disabled || typeof identifier !== 'string' || !identifier.trim()) return;
                form.elements.qrIdentifier.value = identifier.trim(); form.elements.code.value = '';
                updateQrState(form);
                find('[data-error="code"]', form).hidden = true;
                form.elements.code.removeAttribute('aria-invalid');
                saveDraft(form);
            };
            const event = new CustomEvent('accreditation:scan', { bubbles: true, cancelable: true, detail: { receive, ticketId: ticket.id } });
            if (form.dispatchEvent(event)) notice(find('[data-card-feedback]', form), 'errors.scan_unavailable', true);
        });
        find('[data-check-operation]', form).addEventListener('click', async () => {
            if (saving) return;
            const operation = draft.forms[ticket.id] && draft.forms[ticket.id].pending;
            if (!operation) return;
            let retryingWrite = false;
            saving = true;
            try {
                const result = await request('operationStatus', { operationId: operation.operationId });
                if (result.status === 'saved') renderOrder(result.order, ticket.id);
                else if (result.status === 'not_found') {
                    // A missing result may still be in flight. Replay the same operation and payload.
                    retryingWrite = true;
                    const saved = await request('submitTicket', operation);
                    if (!saved.tickets || !saved.tickets.some(item => item.id === ticket.id && item.status === 'accredited')) throw {};
                    renderOrder(saved, ticket.id);
                } else notice(find('[data-card-feedback]', form), 'errors.unknown', true);
            } catch (error) {
                if (['errors.session_expired', 'errors.session_changed'].includes(error.key)) guard(error.key);
                else {
                    if (retryingWrite && (error.key || error.notSent)) {
                        delete draft.forms[ticket.id].pending; persist(); lock(form, false);
                        find('[data-check-operation]', form).hidden = true;
                        errors(form, error.fields || (['errors.band_unknown', 'errors.band_used', 'errors.variant_mismatch', 'errors.ambiguous_code'].includes(error.key) ? { code: error.key } : {}));
                    }
                    notice(find('[data-card-feedback]', form), error.key || 'errors.unknown', true);
                    if (retryingWrite && ['errors.ticket_unavailable', 'errors.unpaid', 'errors.order_cancelled', 'errors.ticket_delegated', 'errors.invitation_unavailable'].includes(error.key)) await loadOrder();
                }
            } finally { saving = false; }
        });
        form.addEventListener('submit', async event => {
            event.preventDefault();
            if (saving || !ticket.canAccredit || (draft.invites[ticket.id] && draft.invites[ticket.id].pending) || (draft.forms[ticket.id] && draft.forms[ticket.id].pending)) return;
            const values = read(form);
            if (!validate(form, values)) return;
            const { code, qrIdentifier, ...participant } = values;
            const operationId = crypto.randomUUID();
            const payload = { ticketId: ticket.id, code, qrIdentifier, participant, operationId, locale: root.dataset.locale };
            draft.forms[ticket.id] = { values, pending: payload }; persist();
            saving = true; lock(form, true); form.setAttribute('aria-busy', 'true');
            all('[data-submit-ticket]').forEach(button => { button.disabled = true; });
            notice(find('[data-card-feedback]', form), 'ticket.saving');
            try {
                const result = await request('submitTicket', payload);
                if (!result.tickets || !result.tickets.some(item => item.id === ticket.id && item.status === 'accredited')) throw {};
                delete draft.forms[ticket.id]; persist(); renderOrder(result, ticket.id);
            } catch (error) {
                if (error.key || error.notSent) {
                    delete draft.forms[ticket.id].pending; persist();
                    if (['errors.session_expired', 'errors.session_changed'].includes(error.key)) guard(error.key);
                    else {
                        lock(form, false);
                        errors(form, error.fields || (['errors.band_unknown', 'errors.band_used', 'errors.variant_mismatch', 'errors.ambiguous_code'].includes(error.key) ? { code: error.key } : {}));
                        notice(find('[data-card-feedback]', form), error.key || 'errors.service_unavailable', true);
                        if (['errors.ticket_unavailable', 'errors.unpaid', 'errors.order_cancelled', 'errors.ticket_delegated', 'errors.invitation_unavailable'].includes(error.key)) { form.querySelector('[data-submit-ticket]').disabled = true; await loadOrder(); }
                    }
                } else {
                    find('[data-check-operation]', form).hidden = false; find('[data-check-operation]', form).disabled = false;
                    notice(find('[data-card-feedback]', form), 'errors.unknown', true);
                }
            } finally {
                saving = false; form.removeAttribute('aria-busy');
                all('[data-ticket-form]').forEach(other => {
                    const item = order && order.tickets.find(slot => slot.id === other.dataset.ticketId);
                    const pending = draft.forms[other.dataset.ticketId] && draft.forms[other.dataset.ticketId].pending;
                    find('[data-submit-ticket]', other).disabled = !!pending || !item || !item.canAccredit || !!(draft.invites[other.dataset.ticketId] && draft.invites[other.dataset.ticketId].pending);
                });
            }
        });
    }
    all('[data-renew-access]').forEach(button => button.addEventListener('click', async () => {
        if (saving) return;
        const token = params.get('token') || draft.accessToken;
        if (!token) return;
        const feedback = find('[data-link-feedback]') || find('[data-dashboard-feedback]');
        saving = true; button.disabled = true;
        try {
            const result = await request('renewAccess', { token });
            if (result.status !== 'access_requested') throw {};
            notice(feedback, 'access.renew_sent');

            const delay = Math.max(0, result.resendAt - Date.now());
            setTimeout(() => { button.disabled = false; }, Math.min(delay, 2147483647));
        } catch (error) {
            notice(feedback, error.key || 'errors.service_unavailable', true);
            button.disabled = false;
            if (error.canRenew === false || error.key === 'errors.link_invalid') button.hidden = true;
        } finally { saving = false; }
    }));
    all('.navigation a[lang]', document).filter(link => link.classList.contains('dropdown-item')).forEach(link => {
        const target = new URL(link.href);
        if (params.has('token')) target.searchParams.set('token', params.get('token'));
        link.href = target.href; link.addEventListener('click', persist);
    });
    window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
    const access = find('[data-access-form]');
    if (access) {
        fill(access, draft.identity);
        access.addEventListener('input', () => { draft.identity = read(access); persist(); });
        find('button[type="submit"]', access).disabled = false;
        access.addEventListener('submit', async event => {
            event.preventDefault();
            if (saving) return;
            const values = read(access);
            if (!validate(access, values)) return;
            draft.identity = values; persist(); saving = true; lock(access, true);
            notice(find('[data-access-feedback]'), 'access.sending');
            try {
                const result = await request('requestAccess', values);
                if (result.status !== 'access_requested') throw { key: 'errors.service_unavailable' };
                draft.accessRequested = true;
                delete draft.accessNotice;
                delete draft.accessToken;
                go('status');
            } catch (error) { notice(find('[data-access-feedback]'), error.key || 'errors.service_unavailable', true); }
            finally { saving = false; lock(access, false); }
        });
    }
    const accessLink = find('[data-access-link]');
    if (accessLink) {
        const failed = (key, canRenew = false) => {
            saving = false;
            find('[data-link-title]').textContent = text('access.title');
            notice(find('[data-link-feedback]'), key, true);
            find('[data-link-recovery]').hidden = false;
            find('[data-renew-access]').hidden = !canRenew;
        };
        const openOrder = async () => {
            const token = params.get('token');
            if (!token) {
                if (draft.accessNotice) {
                    failed(draft.accessNotice, !!draft.accessToken && ['errors.session_expired', 'errors.link_expired', 'errors.link_used'].includes(draft.accessNotice));
                } else {
                    find('[data-link-title]').textContent = text('access.check_email');
                    notice(find('[data-link-feedback]'), draft.accessRequested ? 'access.sent' : 'access.open_email');
                    find('[data-link-recovery]').hidden = false;
                }
                return;
            }
            saving = true;
            try {
                const opened = await request('consumeAccess', { token });
                if (draft.accessIdentity !== opened.accessIdentity) draft = { forms: {}, invites: {}, management: {} };
                draft.accessIdentity = opened.accessIdentity;
                draft.context = opened.context;
                draft.accessToken = token;
                delete draft.accessNotice;
                delete draft.accessRequested;
                persist();
                location.replace(url('details'));
            } catch (error) { failed(error.key || 'errors.service_unavailable', error.canRenew === true); }
        };
        openOrder();
    }
    if (find('[data-dashboard]')) {
        loadOrder();
        find('[data-refresh-order]').addEventListener('click', () => { if (!saving) loadOrder(); });
        window.addEventListener('focus', () => { if (!saving && !order?.forwarded && !scanner?.isOpen() && !find('[data-dashboard]').hidden) loadOrder(); });
        find('[data-logout]').addEventListener('click', async () => {
            if (saving) return;
            saving = true;
            try { await request('logout'); draft = { forms: {} }; go('start'); }
            catch (error) { saving = false; notice(find('[data-order-notice]'), error.key || 'errors.service_unavailable', true); }
        });
    }
}
